<script setup lang="ts">
import type { ContentNeighbor } from '~/types/blog'
import { NuxtLink } from '#components'
import { buildPostPath } from '~/composables/usePermalink'

const props = defineProps<{
  prev: ContentNeighbor | null
  next: ContentNeighbor | null
  fields?: Record<string, unknown>
}>()

const links = computed(() => [
  {
    direction: 'previous' as const,
    rel: 'prev',
    label: '上一篇',
    item: props.prev,
    emptyText: '正在开发新大陆',
    emptyIcon: 'fa-map-location-dot'
  },
  {
    direction: 'next' as const,
    rel: 'next',
    label: '下一篇',
    item: props.next,
    emptyText: '已抵达世界尽头',
    emptyIcon: 'fa-person-digging'
  }
].map(link => ({
  ...link,
  available: Boolean(link.item?.ID),
  path: link.item?.ID ? buildPostPath(props.fields, link.item, { category: link.item.categorySlug }) : undefined
})))
</script>

<template>
  <nav v-if="prev?.ID || next?.ID" class="post-navigation" aria-label="文章切换">
    <component
      v-for="link in links"
      :key="link.direction"
      :is="link.available ? NuxtLink : 'span'"
      :to="link.path"
      :rel="link.available ? link.rel : undefined"
      :aria-label="link.available ? `${link.label}：${link.item?.title}` : link.emptyText"
      :aria-disabled="link.available ? undefined : true"
      :class="['post-navigation-link', link.direction, { 'is-unavailable': !link.available }]"
    >
      <BlogNavigationArrow v-if="link.available" class="navigation-icon" :direction="link.direction" />
      <i v-else :class="['navigation-icon', 'fa-solid', link.emptyIcon]" aria-hidden="true" />
      <span class="navigation-text">
        <strong>{{ link.available ? link.item?.title : link.emptyText }}</strong>
        <time v-if="link.item?.publishedAt" :datetime="link.item.publishedAt">
          {{ new Date(link.item.publishedAt).toLocaleDateString('zh-CN') }}
        </time>
      </span>
    </component>
  </nav>
</template>

<style scoped>
.post-navigation {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin: 3rem 0;
}

.post-navigation-link {
  display: flex;
  align-items: center;
  gap: 0.5em;
  max-width: 100%;
  color: var(--text);
  text-decoration: none;
  transition: color 0.2s;
}

.post-navigation-link.is-unavailable {
  opacity: 0.4;
  user-select: none;
}

.navigation-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  font-size: 2rem;
  opacity: 0.5;
}

.navigation-text {
  min-width: 0;
  text-wrap: balance;
  transition: transform 0.2s;
}

.navigation-text time {
  display: block;
  opacity: 0.6;
  font-size: 0.8rem;
}

.navigation-text strong {
  font-family: var(--font-heading);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.next {
  flex-direction: row-reverse;
  margin-left: auto;
  text-align: right;
}

.post-navigation-link:not(.is-unavailable):hover {
  color: var(--brand);
}

.post-navigation-link:not(.is-unavailable):hover .navigation-text {
  transform: translateX(-1em);
}

.next:not(.is-unavailable):hover .navigation-text {
  transform: translateX(1em);
}

@media (prefers-reduced-motion: reduce) {
  .post-navigation-link,
  .navigation-text {
    transition: none;
  }

  .post-navigation-link:not(.is-unavailable):hover .navigation-text {
    transform: none;
  }
}
</style>
